import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Edit, Trash2, Eye, WifiOff, AlertCircle,
  CheckCircle, Clock, Settings, Database, Calendar, Users, Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useIntegration, useIntegrationPlatforms } from '../hooks/Integration/useIntegration';
import { useDispatch } from 'react-redux';
import { deleteIntegration } from '../redux/integration/integrationSlice';
import { AppDispatch } from '../redux';

// Per-platform card styling keyed by the API's type_name
const PLATFORM_STYLES: Record<string, { icon: string; bgColor: string }> = {
  simple_practice: { icon: '🏥', bgColor: 'from-blue-400 to-blue-600' },
  therapy_notes: { icon: '📝', bgColor: 'from-emerald-400 to-green-600' },
  theranest: { icon: '🧠', bgColor: 'from-teal-400 to-teal-600' },
  jane_app: { icon: '⚡', bgColor: 'from-indigo-400 to-indigo-600' }
};
const DEFAULT_PLATFORM_STYLE = { icon: '🔌', bgColor: 'from-slate-400 to-slate-600' };
const ACTIVE_ACTION_COLOR = 'from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700';
const DISABLED_ACTION_COLOR = 'from-gray-200 to-gray-300 text-gray-500';

interface IntegrationCardProps {
  integration: {
    id?: string;
    name?: string;
    integrationName?: string;
    integrationType?: string;
    practiceId?: string;
    connectionStatus?: string;
    status?: string;
    username?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({ 
  integration, 
  onView, 
  onEdit, 
  onDelete 
}) => {
  const getStatusColor = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'connected':
        return 'emerald';
      case 'inactive':
      case 'disconnected':
        return 'slate';
      case 'pending':
        return 'amber';
      case 'error':
        return 'rose';
      default:
        return 'slate';
    }
  };

  const getStatusIcon = (status: string | undefined) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'connected':
        return CheckCircle;
      case 'inactive':
      case 'disconnected':
        return WifiOff;
      case 'pending':
        return Clock;
      case 'error':
        return AlertCircle;
      default:
        return WifiOff;
    }
  };

  const getIntegrationIcon = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
      case 'simplepractice':
      case 'simple_practice':
        return Database;
      case 'calendar':
        return Calendar;
      case 'users':
        return Users;
      default:
        return Settings;
    }
  };

  const status = integration.connectionStatus || integration.status || 'inactive';
  const statusColor = getStatusColor(status);
  const StatusIcon = getStatusIcon(status);
  const IntegrationIcon = getIntegrationIcon(integration.integrationType);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="group relative"
    >
      {/* Background blur effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl"></div>
      
      {/* Content */}
      <div className="relative bg-gradient-to-br from-white/60 to-white/30 backdrop-blur-sm rounded-3xl border border-white/40 shadow-lg hover:shadow-2xl transition-all duration-300 p-8 group-hover:scale-[1.02]">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center space-x-4">
            <div className={`p-4 rounded-2xl bg-gradient-to-br from-${statusColor}-100 to-${statusColor}-50 shadow-inner`}>
              <IntegrationIcon className={`h-7 w-7 text-${statusColor}-600`} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-1">
                {integration.integrationName || integration.name || 'Unnamed Integration'}
              </h3>
              <p className="text-sm text-gray-600 font-medium">
                {integration.integrationType || 'Unknown Type'}
              </p>
            </div>
          </div>
          
          <div className={`px-4 py-2 rounded-2xl text-xs font-bold bg-gradient-to-r from-${statusColor}-100 to-${statusColor}-50 text-${statusColor}-700 flex items-center space-x-2 shadow-inner border border-${statusColor}-200`}>
            <StatusIcon className="h-4 w-4" />
            <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex justify-between text-sm bg-white/30 rounded-xl p-3">
            <span className="text-gray-600 font-medium">Practice ID:</span>
            <span className="font-bold text-gray-800">{integration.practiceId || 'N/A'}</span>
          </div>
          
          {integration.username && (
            <div className="flex justify-between text-sm bg-white/30 rounded-xl p-3">
              <span className="text-gray-600 font-medium">Username:</span>
              <span className="font-bold text-gray-800">{integration.username}</span>
            </div>
          )}
          
          {integration.createdAt && (
            <div className="flex justify-between text-sm bg-white/30 rounded-xl p-3">
              <span className="text-gray-600 font-medium">Created:</span>
              <span className="font-bold text-gray-800">
                {new Date(integration.createdAt).toLocaleDateString()}
              </span>
            </div>
          )}
          
          {integration.updatedAt && (
            <div className="flex justify-between text-sm bg-white/30 rounded-xl p-3">
              <span className="text-gray-600 font-medium">Updated:</span>
              <span className="font-bold text-gray-800">
                {new Date(integration.updatedAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-white/40">
          <div className="flex space-x-3">
            <button
              onClick={() => integration.id && onView(integration.id)}
              disabled={!integration.id}
              className="flex items-center space-x-2 px-4 py-2.5 text-sm font-bold text-blue-600 hover:text-blue-700 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl transition-all duration-200 shadow-inner border border-blue-200 disabled:opacity-50"
            >
              <Eye className="h-4 w-4" />
              <span>View</span>
            </button>
            
            <button
              onClick={() => integration.id && onEdit(integration.id)}
              disabled={!integration.id}
              className="flex items-center space-x-2 px-4 py-2.5 text-sm font-bold text-emerald-600 hover:text-emerald-700 bg-gradient-to-r from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 rounded-xl transition-all duration-200 shadow-inner border border-emerald-200 disabled:opacity-50"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </button>
          </div>
          
          <button
            onClick={() => integration.id && onDelete(integration.id)}
            disabled={!integration.id}
            className="flex items-center space-x-2 px-4 py-2.5 text-sm font-bold text-rose-600 hover:text-rose-700 bg-gradient-to-r from-rose-50 to-rose-100 hover:from-rose-100 hover:to-rose-200 rounded-xl transition-all duration-200 shadow-inner border border-rose-200 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const IntegrationsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { integrations, loading } = useIntegration();
  const { platforms, loading: platformsLoading, error: platformsError } = useIntegrationPlatforms();

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = (id: string) => {
    navigate(`/practice/integration/simplepractice/details?id=${String(id)}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/practice/integration/simplepractice/form?id=${String(id)}`);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      setIsDeleting(true);
      try {
        await dispatch(deleteIntegration(id)).unwrap();
        setDeleteConfirmId(null);
      } catch (error) {
        console.error('Failed to delete integration:', error);
      } finally {
        setIsDeleting(false);
      }
    } else {
      setDeleteConfirmId(id);
    }
  };

  const handleCreateNew = () => {
    navigate('/practice/integration/simplepractice/form');
  };

  const handleBack = () => {
    navigate('/practice/dashboard');
  };

  const integrationOptions = platforms.map((platform) => {
    const style = PLATFORM_STYLES[platform.type_name] || DEFAULT_PLATFORM_STYLE;
    const isActive = platform.isActive === true;
    return {
      id: platform._id,
      typeName: platform.type_name,
      name: platform.name,
      icon: style.icon,
      bgColor: style.bgColor,
      status: isActive ? 'connected' : 'coming-soon',
      description: platform.desc,
      action: isActive ? 'Configure' : 'Coming Soon',
      actionColor: isActive ? ACTIVE_ACTION_COLOR : DISABLED_ACTION_COLOR,
      disabled: !isActive,
      opacity: isActive ? 'opacity-100' : 'opacity-60'
    };
  });

  return (
    <DashboardLayout>
      <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 min-h-screen">
        <div className="p-8">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center"
          >
            <h1 className="text-4xl font-black text-gray-800 mb-4 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Platform Integrations
            </h1>
            <p className="text-lg text-gray-600 font-medium">Connect your practice management and marketing tools seamlessly</p>
          </motion.div>

          {/* Integration Cards Grid */}
          {platformsLoading ? (
            <div className="flex items-center justify-center py-16 mb-16">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : platformsError ? (
            <div className="flex flex-col items-center justify-center py-16 mb-16 text-center">
              <AlertCircle className="w-10 h-10 text-red-300 mb-4" />
              <p className="text-gray-600 font-medium">Could not load available integrations. Please try again later.</p>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {integrationOptions.map((integration, index) => (
              <motion.div
                key={integration.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative ${integration.opacity} cursor-pointer`}
                onClick={() => {
                  if (!integration.disabled && integration.typeName === 'simple_practice') {
                    handleCreateNew();
                  }
                }}
              >
                {/* Glassmorphic background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/50 to-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl group-hover:shadow-2xl transition-all duration-300"></div>
                
                {/* Content */}
                <div className="relative bg-gradient-to-br from-white/70 to-white/40 backdrop-blur-sm rounded-3xl border border-white/50 p-8 group-hover:scale-105 transition-all duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${integration.bgColor} rounded-2xl flex items-center justify-center text-white text-3xl shadow-lg`}>
                      {integration.icon}
                    </div>
                    {integration.status === 'connected' ? (
                      <div className="w-8 h-8 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <div className="px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 text-xs font-bold rounded-2xl border border-gray-300 shadow-inner">
                        Coming Soon
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-black text-gray-800 mb-2">{integration.name}</h3>
                  <p className="text-sm text-gray-600 font-medium mb-6">{integration.description}</p>
                  
                  <div className="mt-6 pt-6 border-t border-white/50">
                    <button 
                      disabled={integration.disabled}
                      className={`w-full bg-gradient-to-r ${integration.actionColor} text-white font-bold py-3 px-6 rounded-2xl transition-all duration-200 text-sm shadow-lg hover:shadow-xl transform hover:scale-105 ${integration.disabled ? 'cursor-not-allowed' : ''}`}
                    >
                      {integration.action} →
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )}

          {/* Existing Integrations Section */}
          {integrations.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative"
            >
              {/* Section background */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-xl rounded-3xl border border-white/40 shadow-xl"></div>
              
              <div className="relative p-8">
                <h2 className="text-2xl font-black text-gray-800 mb-8 text-center">Your Active Integrations</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {integrations.map((integration) => (
                    <IntegrationCard
                      key={integration.id || `integration-${Math.random()}`}
                      integration={integration}
                      onView={handleView}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Delete Confirmation Modal */}
          {deleteConfirmId && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative"
              >
                {/* Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/90 to-white/70 backdrop-blur-xl rounded-3xl border border-white/50 shadow-2xl"></div>
                
                {/* Content */}
                <div className="relative bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm rounded-3xl border border-white/60 p-8 max-w-md w-full mx-4">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="p-3 bg-gradient-to-r from-rose-100 to-red-100 rounded-2xl shadow-inner border border-red-200">
                      <Trash2 className="h-7 w-7 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-gray-900">Delete Integration</h3>
                      <p className="text-sm text-gray-600 font-medium">This action cannot be undone</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 font-medium mb-8">
                    Are you sure you want to delete this integration? This will remove all associated data and connections.
                  </p>
                  
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setDeleteConfirmId(null)}
                      disabled={isDeleting}
                      className="flex-1 px-6 py-3 text-gray-700 font-bold bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl hover:from-gray-200 hover:to-gray-300 transition-all duration-200 shadow-inner border border-gray-300 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDelete(deleteConfirmId)}
                      disabled={isDeleting}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-bold rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 disabled:opacity-50"
                    >
                      {isDeleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Deleting...</span>
                        </>
                      ) : (
                        <span>Delete</span>
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};
